import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { useQueryClient } from 'react-query';
import { isAxiosError } from 'axios';
import { useFeatureFlag } from 'configcat-react';
import {
	AppointmentControllerApi as AppointmentApi,
	AppointmentCreateRequestExpertCalendarSlotTypeEnum,
	AppointmentCreateRequestLiveServiceTypeEnum,
	AppointmentResponseAppointmentStatusEnum,
	AppointmentSubjectResponseSubjectTypeEnum as SubjectType,
	ExpertDailySlotResponse,
	ExpertDailySlotResponseSlotStatusEnum,
	MeetingSubjectResponse,
	NearestAvailableSlotsResponse
} from '@dopingtech/live_expert_bff_api_ts';
import { useApi } from '@/api/useApi';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys';
import { CONFIGCAT_KEYS } from '@/constants/configcat-keys';
import ExceptionManager from '@/managers/exception-manager';
import { PlannedLessonItem, PlannedLessonMessage, PlannedLessonSlot, SelectItem } from '../boutique-classroom.types';
import {
	PLANNED_LESSON_DATE_PARAM_FORMAT,
	PLANNED_LESSON_DATE_TIME_PARAM_FORMAT,
	PLANNED_LESSON_DEFAULT_DAY_RANGE,
	PLANNED_LESSON_ERROR
} from '../components/planned-lesson-modal/planned-lesson-modal.constants';
import { toTimeLabel } from '../components/planned-lesson-modal/planned-lesson-modal.utils';

const PLANNED_LECTURE = AppointmentCreateRequestLiveServiceTypeEnum.PlannedLecture;
const CREATED_STATUS = [AppointmentResponseAppointmentStatusEnum.Created];

type ExpertCalendarSlot = { id: number; type: AppointmentCreateRequestExpertCalendarSlotTypeEnum };
type SlotsByDate = Record<string, PlannedLessonSlot[]>;

const EMPTY_SLOTS: SlotsByDate = {};
const EMPTY_TOPIC_IDS: number[] = [];
const EMPTY_SUBJECTS: MeetingSubjectResponse[] = [];

function getServiceErrorMessage(error: unknown): string | undefined {
	if (!isAxiosError<{ message?: string; errors?: { errorMsg?: string }[] }>(error)) return undefined;
	const body = error.response?.data;
	return body?.errors?.[0]?.errorMsg || body?.message;
}

function mapSlot(response: ExpertDailySlotResponse): PlannedLessonSlot | null {
	if (response.slot?.id === undefined || !response.slot.slotStartTime) return null;
	if (response.slotStatus !== ExpertDailySlotResponseSlotStatusEnum.Available) return null;
	return { id: response.slot.id, startTime: toTimeLabel(response.slot.slotStartTime), endTime: toTimeLabel(response.slot.slotEndTime) };
}

function mapSlotsByDate(days: NearestAvailableSlotsResponse[]): SlotsByDate {
	return days.reduce<SlotsByDate>((acc, day) => {
		const slots = (day.slots ?? []).map(mapSlot).filter((slot): slot is PlannedLessonSlot => slot !== null);
		if (day.slotDate && slots.length) acc[day.slotDate.slice(0, PLANNED_LESSON_DATE_PARAM_FORMAT.length)] = slots.sort((a, b) => a.startTime.localeCompare(b.startTime));
		return acc;
	}, {});
}

async function fetchStudentLessons(api: AppointmentApi, studentId: number, date: string): Promise<number[]> {
	const day = dayjs(date);
	const { data } = await api.searchAppointments(
		studentId,
		CREATED_STATUS,
		[PLANNED_LECTURE],
		undefined,
		undefined,
		day.startOf('day').format(PLANNED_LESSON_DATE_TIME_PARAM_FORMAT),
		day.endOf('day').format(PLANNED_LESSON_DATE_TIME_PARAM_FORMAT)
	);
	return (data?.data?.content ?? []).map(item => item.topicId).filter((id): id is number => id !== undefined);
}

export function hasStudentConflict(topicIds: number[], subjectIds: Array<SelectItem['id'] | undefined>): boolean {
	return topicIds.some(id => subjectIds.includes(id));
}

type MeetingSubjectLevel = typeof SubjectType.Lesson | typeof SubjectType.Unit | typeof SubjectType.Subject;

export function useMeetingSubjects(studentId: number, subjectType: MeetingSubjectLevel, parentId?: SelectItem['id'], branchId?: number | null) {
	const { MeetingSubjectControllerApi } = useApi();
	const isLesson = subjectType === SubjectType.Lesson;
	const isEnabled = isLesson ? Boolean(branchId) : parentId !== undefined;

	const { data } = useQueryProvider(
		[QUERY_KEYS.getMeetingSubjects, studentId, subjectType, parentId ?? ''],
		() => MeetingSubjectControllerApi.getMeetingSubjects(studentId, subjectType, isLesson ? undefined : (parentId as number), PLANNED_LECTURE),
		{ select: ({ data: response }) => response?.data ?? EMPTY_SUBJECTS, enabled: isEnabled, staleTime: Infinity, refetchOnWindowFocus: false }
	);

	return useMemo<SelectItem[]>(
		() =>
			(data ?? EMPTY_SUBJECTS)
				.filter(subject => subject.id !== undefined && (!isLesson || subject.branchId === branchId))
				.map(subject => ({ id: subject.id as number, name: subject.subjectName ?? '' })),
		[branchId, data, isLesson]
	);
}

interface UseExpertPlannedSlotsParams {
	studentId?: number;
	branchId?: number | null;
	expertId?: number | null;
}

export function useExpertPlannedSlots({ studentId, branchId, expertId }: UseExpertPlannedSlotsParams) {
	const { CalendarControllerApi } = useApi();
	const { value: dayRange } = useFeatureFlag(CONFIGCAT_KEYS.MAX_COACH_ACTIVE_SLOT_DAYS, PLANNED_LESSON_DEFAULT_DAY_RANGE);
	const today = dayjs().format(PLANNED_LESSON_DATE_PARAM_FORMAT);
	const isEnabled = Boolean(studentId && branchId && expertId);

	const { data, isLoading } = useQueryProvider(
		[QUERY_KEYS.findNearestAvailableSlots, studentId ?? '', branchId ?? '', expertId ?? '', today, dayRange],
		() => CalendarControllerApi.findNearestAvailableSlots(studentId as number, today, PLANNED_LECTURE, dayRange, branchId as number, expertId as number),
		{ select: ({ data: response }) => mapSlotsByDate(response?.data ?? []), enabled: isEnabled, refetchOnWindowFocus: false }
	);

	return { slotsByDate: data ?? EMPTY_SLOTS, dayRange, isLoading: isEnabled && isLoading };
}

export function useStudentPlannedLessons(studentId?: number, date?: string | null) {
	const { AppointmentControllerApi } = useApi();
	const isEnabled = Boolean(studentId && date);

	const { data } = useQueryProvider(
		[QUERY_KEYS.searchAppointments, PLANNED_LECTURE, studentId ?? '', date ?? ''],
		() => fetchStudentLessons(AppointmentControllerApi, studentId as number, date as string),
		{
			enabled: isEnabled,
			refetchOnWindowFocus: false,
			staleTime: 0
		}
	);

	return data ?? EMPTY_TOPIC_IDS;
}

export interface CreatePlannedLessonsResult {
	createdStudentIds: number[];
	errors: Record<number, PlannedLessonMessage>;
}

export function useCreatePlannedLessons() {
	const { AppointmentControllerApi, CalendarControllerApi } = useApi();
	const queryClient = useQueryClient();

	const findExpertSlot = useCallback(
		async (lesson: PlannedLessonItem): Promise<ExpertCalendarSlot | null> => {
			const { data } = await CalendarControllerApi.findFirstAvailableExpertForSlot(lesson.studentId, lesson.date, lesson.slot.id, PLANNED_LECTURE, lesson.branchId, lesson.expertId);
			const expert = data?.data;
			if (!expert?.expertCalendarSlotId || (expert.expertId !== undefined && expert.expertId !== lesson.expertId)) return null;
			return {
				id: expert.expertCalendarSlotId,
				type: (expert.expertCalendarSlotType as AppointmentCreateRequestExpertCalendarSlotTypeEnum | undefined) ?? AppointmentCreateRequestExpertCalendarSlotTypeEnum.Manual
			};
		},
		[CalendarControllerApi]
	);

	const run = useCallback(
		async (lessons: PlannedLessonItem[]): Promise<CreatePlannedLessonsResult> => {
			const errors: Record<number, PlannedLessonMessage> = {};
			const expertSlots: ExpertCalendarSlot[] = [];
			for (const lesson of lessons) {
				if (hasStudentConflict(await fetchStudentLessons(AppointmentControllerApi, lesson.studentId, lesson.date), [lesson.lessonId, lesson.topicId])) {
					errors[lesson.studentId] = PLANNED_LESSON_ERROR.STUDENT_CONFLICT;
					continue;
				}
				const expertSlot = await findExpertSlot(lesson);
				if (!expertSlot) {
					errors[lesson.studentId] = PLANNED_LESSON_ERROR.EXPERT_CONFLICT(lesson.expertName);
					continue;
				}
				expertSlots.push(expertSlot);
			}
			if (Object.keys(errors).length) return { createdStudentIds: [], errors };

			const createdStudentIds: number[] = [];
			for (const [index, lesson] of lessons.entries()) {
				const expertSlot = expertSlots[index];
				try {
					await AppointmentControllerApi.createAppointment({
						studentId: lesson.studentId,
						expertId: lesson.expertId,
						branchId: lesson.branchId,
						meetingSubjectId: lesson.topicId,
						expertCalendarSlotId: expertSlot.id,
						expertCalendarSlotType: expertSlot.type,
						appointmentDate: lesson.date,
						liveServiceType: PLANNED_LECTURE
					});
					createdStudentIds.push(lesson.studentId);
				} catch (error) {
					ExceptionManager.errorHandler(error);
					return { createdStudentIds, errors: { [lesson.studentId]: PLANNED_LESSON_ERROR.CREATE_FAILED(lesson.studentName, getServiceErrorMessage(error)) } };
				}
			}
			return { createdStudentIds, errors };
		},
		[AppointmentControllerApi, findExpertSlot]
	);

	const mutation = useMutationProvider(run, {
		onSettled: () =>
			Promise.all([
				queryClient.invalidateQueries([QUERY_KEYS.findNearestAvailableSlots]),
				queryClient.invalidateQueries([QUERY_KEYS.searchAppointments]),
				queryClient.invalidateQueries([QUERY_KEYS.findAppointmentsOfTheDay])
			])
	});

	return useMemo(() => ({ createPlannedLessons: mutation.safeMutate, isCreating: mutation.isLoading }), [mutation.safeMutate, mutation.isLoading]);
}
